import { PriceListProduct } from "./PriceListProduct";
import { PrintFormat } from "./PrintFormat";

export type PriceListData = Omit<PriceList, 'data'>;

type OptionalPrice = {
    enabled: true;
    price: number;
} | {
    enabled: false;
};

export interface Packet {
    name: string;
    photoCount: number;
    packetPrice: OptionalPrice;
    priceForExtra: OptionalPrice;
    printBudget: OptionalPrice;
}

export interface PriceList {
    id?: string;
    name: string;
    photoBasePrice: OptionalPrice;
    printSaleDisabled: boolean;
    digitalPrice: OptionalPrice;
    applyLowestWhenDigital: boolean;
    nonZeroOrder: boolean;
    mustPickPrints: boolean;
    isSellingPackets: boolean;
    photosInPacket: number | null;
    packetPrice: OptionalPrice;
    isSellingOverPacket: boolean;
    extraPhotoPrice: number | null;
    printBudget: OptionalPrice;
    digitalDebited: boolean;
    formats: PrintFormat[];
    products: PriceListProduct[];
}

function mergeArrays<T>(arr1: Array<T>, arr2: Array<T>) {
    const maxLength = Math.max(arr1.length, arr2.length);
  
    return Array.from({ length: maxLength }, (_, i) => [
      arr1[i],
      arr2[i],
    ]);
}

export const priceListEquals = (a: PriceList, b: PriceList) => {
    const sortFormats = (formats: Array<PrintFormat>) => [...formats].sort(
        (a, b) => {
            if (a.id! < b.id!) return -1;
            if (a.id! > b.id!) return 1;
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
        }
    )
    const optionalPriceEquals = (a: OptionalPrice | null, b: OptionalPrice | null) => 
        (a === null && b === null) || 
        (
            a !== null &&
            b !== null &&
            (
                (!a.enabled && !b.enabled) ||
                (a.enabled && b.enabled && Number(a.price) === Number(b.price))
            )
        );

    const aEntries = Object.entries(a);
    const bEntries = Object.entries(b);

    const allKeys = Array.from(new Set([...aEntries, ...bEntries].map(([key]) => key))) as Array<keyof PriceList>;
    const comparisonMap = allKeys.reduce((prev, key) => ({ ...prev, [key]: [a[key], b[key]]}), <Record<keyof PriceList, any>>{});
    const comparisonEntries = Object.entries(comparisonMap);
    const scalarEquals = comparisonEntries.filter(([key]) => [
        'id',
        'name',
        'printSaleDisabled',
        'applyLowestWhenDigital',
        'nonZeroOrder',
        'mustPickPrints',
        'isSellingPackets',
        'photosInPacket',
        'isSellingOverPacket',
        'extraPhotoPrice',
        'digitalDebited',
    ].includes(key)).reduce((prev, [key, [a, b]]) =>
        prev && (key === 'extraPhotoPrice'
            ? (a == null && b == null) || (a != null && b != null && Number(a) === Number(b))
            : key === 'isSellingOverPacket' ? !!a === !!b : a === b), true);

    const priceEquals = comparisonEntries.filter(([key]) => [
        'photoBasePrice',
        'digitalPrice',
        'packetPrice',
        'printBudget',
    ].includes(key)).reduce((prev, [_, [a, b]]) => prev && optionalPriceEquals(a, b), true);
    const formats = mergeArrays(sortFormats(a.formats), sortFormats(b.formats));
    const formatsEqual = formats.reduce((prev, [a, b]) => prev && a && b && a.equals(b), true);

    const sortProducts = (products: PriceListProduct[]) => [...products].sort((left, right) =>
        `${left.id || ''}:${left.name}:${left.global}`.localeCompare(`${right.id || ''}:${right.name}:${right.global}`)
    );
    const productsEqual = mergeArrays(sortProducts(a.products), sortProducts(b.products)).every(([left, right]) =>
        !!left && !!right && left.id === right.id && left.name === right.name &&
        Number(left.price) === Number(right.price) && left.global === right.global
    );

    return scalarEquals && priceEquals && formatsEqual && productsEqual;
}

// export class PriceList {
//     public id?: string;

//     public photoBasePrice: OptionalPrice  = { enabled: false };


//     public sellsDigital: boolean = false;
//     public digitalPrice?: number|null;
//     public formats: PrintFormat[] = [];
//     public applyLowestWhenDigital = false;
//     public products: PriceListProduct[] = [];



//     constructor(public name: string) {}

//     get clone() {
//         const clone = new PriceList(this.name);

//         Object.assign(clone, {
//             sellsDigital: this.sellsDigital,
//             digitalPrice: this.digitalPrice,
//             formats: this.formats.map(f => f.clone),
//             products: this.products.map(({ name, price, global }) => new PriceListProduct(name, price, global)),
//         })

//         return clone;
//     }

//     get data(): PriceListData {
//         return omit<PriceList, 'data'>(this, 'data');
//     }

//     static fromData(data: any) {
//         const priceList = new this(data.name);
//         Object.assign(priceList, omit(data, 'formats', 'name'));
//         priceList.formats = data.formats.map((format: any) => PrintFormat.fromData(format));
//         priceList.products = data.products.map(({ name, price, global }: any) => new PriceListProduct(name, price, global));

//         return priceList;
//     }
// }
